import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GrindrodLogo from "../components/GrindrodLogo";
import { Anchor, SignOut, ArrowLeft } from "@phosphor-icons/react";

const TERMINAL_LABELS = {
    richards_bay: "Grindrod Richards Bay Terminal",
    maputo: "Grindrod Maputo Terminal",
};

const TERMINAL_TAGLINES = {
    maputo: "Maputo bulk & break-bulk operations · onboarding your team first.",
};

export default function ComingSoon() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const terminal = user?.active_terminal || user?.terminal || "maputo";
    const label = TERMINAL_LABELS[terminal] || terminal;
    const tagline = TERMINAL_TAGLINES[terminal] || "";

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen bg-[var(--grindrod-navy)] text-white relative overflow-hidden" data-testid="coming-soon-page">
            {/* Decorative background */}
            <div className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(circle_at_20%_20%,#E30613,transparent_50%),radial-gradient(circle_at_80%_80%,#3B82F6,transparent_50%)]" />

            <div className="relative z-10 min-h-screen flex flex-col">
                <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/10">
                    <GrindrodLogo size="lg" layout="row" subtitle={label.toUpperCase()} inverted />
                    <button
                        onClick={handleLogout}
                        className="inline-flex items-center gap-2 text-xs uppercase-label text-white/70 hover:text-white transition"
                        data-testid="coming-soon-logout"
                    >
                        <SignOut size={14} /> Sign out
                    </button>
                </header>

                <main className="flex-1 flex items-center justify-center px-6 md:px-12">
                    <div className="max-w-2xl w-full text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[var(--grindrod-red)] text-white text-[10px] font-bold uppercase tracking-[0.24em] rounded-sm mb-8">
                            <Anchor size={14} weight="duotone" /> Terminal · {label}
                        </div>
                        <h1 className="font-display font-black text-5xl md:text-7xl leading-none tracking-tight mb-6">
                            Coming<br />
                            <span className="text-[var(--grindrod-red)]">Soon.</span>
                        </h1>
                        <p className="text-lg text-white/80 max-w-lg mx-auto mb-4">
                            {label} portal is under construction. Registration and access management is live so your team can onboard early.
                        </p>
                        {tagline && (
                            <p className="text-sm text-white/50 italic mb-10">{tagline}</p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 mb-12">
                            <MilestoneCard step="01" title="Registration open" status="live" />
                            <MilestoneCard step="02" title="Vessel line-up" status="planned" />
                            <MilestoneCard step="03" title="Live tracking" status="planned" />
                        </div>

                        <button
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 border border-white/20 hover:border-[var(--grindrod-red)] hover:bg-[var(--grindrod-red)] px-6 py-3 rounded-sm text-sm uppercase-label transition"
                            data-testid="coming-soon-back-btn"
                        >
                            <ArrowLeft size={14} /> Back to sign in
                        </button>
                    </div>
                </main>

                <footer className="px-6 md:px-12 py-4 text-center text-[10px] uppercase-label text-white/40">
                    Grindrod Terminals · One portal, every terminal · Richards Bay live · {label} soon
                </footer>
            </div>
        </div>
    );
}

function MilestoneCard({ step, title, status }) {
    const isLive = status === "live";
    return (
        <div className={`border rounded-sm p-4 text-left ${isLive ? "border-[var(--grindrod-red)] bg-[var(--grindrod-red)]/10" : "border-white/10 bg-white/[0.03]"}`}>
            <div className="text-[10px] uppercase-label text-white/50 mb-1">Step {step}</div>
            <div className="font-display font-bold text-base leading-tight">{title}</div>
            <div className={`text-[10px] mt-2 uppercase-label ${isLive ? "text-[var(--grindrod-red)]" : "text-white/40"}`}>
                {isLive ? "Available now" : "Planned"}
            </div>
        </div>
    );
}
